import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Layers, RefreshCw, Check, X, EyeOff } from "lucide-react";

export default function AdminDedupe() {
  const [kind, setKind] = useState("services");
  const [status, setStatus] = useState("pending");
  const [groups, setGroups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [canon, setCanon] = useState({});

  const load = useCallback(() => {
    api.get(`/admin/dedupe/groups?kind=${kind}&status=${status}`).then((r) => setGroups(r.data.groups || [])).catch(() => setGroups([]));
    api.get("/admin/dedupe/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [kind, status]);
  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setBusy(true);
    try { const r = await api.post(`/admin/dedupe/scan?kind=${kind}`); const n = r.data.groups_found?.[kind] ?? 0; toast.success(`Scan complete — ${n} duplicate group(s) found`); load(); }
    catch { toast.error("Scan failed"); } finally { setBusy(false); }
  };
  const act = async (gid, path, body) => {
    try { await api.post(`/admin/dedupe/groups/${gid}/${path}`, body || {}); toast.success(path === "merge" ? "Merged — duplicates collapsed in search" : path === "unmerge" ? "Unmerged — all listings restored" : "Ignored"); load(); }
    catch { toast.error("Action failed"); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6" data-testid="admin-dedupe">
      <div className="mb-4 flex items-center gap-2">
        <Copy className="h-5 w-5 text-blue-600" />
        <h1 className="text-xl font-bold text-slate-900">Duplicate listings</h1>
      </div>
      <p className="mb-4 max-w-2xl text-sm text-slate-500">Find reposted trips and near-identical listings. Merging keeps one canonical and hides the rest from search — fully reversible, nothing is deleted.</p>

      {summary && (
        <div className="mb-4 flex flex-wrap gap-2 text-xs" data-testid="dedupe-summary">
          <span className="rounded-full bg-amber-50 px-3 py-1 font-semibold text-amber-700">Pending: {summary.pending}</span>
          <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">Merged: {summary.merged}</span>
          <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-600">Ignored: {summary.ignored}</span>
          <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-blue-700">Hidden duplicates: {summary.duplicate_listings}</span>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex rounded-full border border-slate-200 p-0.5">
          {["services", "products"].map((k) => <button key={k} onClick={() => setKind(k)} data-testid={`dedupe-kind-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${kind === k ? "bg-blue-600 text-white" : "text-slate-600"}`}>{k === "services" ? "Trips & Services" : "Products"}</button>)}
        </div>
        <div className="flex rounded-full border border-slate-200 p-0.5">
          {["pending", "merged", "ignored"].map((s) => <button key={s} onClick={() => setStatus(s)} data-testid={`dedupe-status-${s}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${status === s ? "bg-slate-900 text-white" : "text-slate-600"}`}>{s}</button>)}
        </div>
        <button onClick={scan} disabled={busy} data-testid="dedupe-scan-btn" className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Scanning…" : "Scan now"}</button>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400" data-testid="dedupe-empty"><Layers className="mx-auto mb-2 h-6 w-6 text-slate-300" />No {status} duplicate groups. Run a scan to detect reposted / near-identical listings.</div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const chosen = canon[g.group_id] || g.canonical_id;
            return (
              <div key={g.group_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`dedupe-group-${g.group_id}`}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-slate-800">{g.sample_name} <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{g.count} listings</span></p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${g.status === "merged" ? "bg-emerald-50 text-emerald-700" : g.status === "ignored" ? "bg-slate-100 text-slate-500" : "bg-amber-50 text-amber-700"}`}>{g.status}</span>
                </div>
                <div className="space-y-1.5">
                  {g.members.map((m) => (
                    <label key={m.id} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm ${chosen === m.id ? "border-blue-300 bg-blue-50/50" : "border-slate-100"}`} data-testid={`dedupe-member-${m.id}`}>
                      <span className="flex min-w-0 items-center gap-2">
                        <input type="radio" name={`canon-${g.group_id}`} checked={chosen === m.id} onChange={() => setCanon((c) => ({ ...c, [g.group_id]: m.id }))} data-testid={`dedupe-canon-${m.id}`} disabled={g.status !== "pending"} />
                        <span className="truncate text-slate-800">{m.name}</span>
                        {chosen === m.id && <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">Keep this</span>}
                      </span>
                      <span className="shrink-0 text-xs text-slate-400">{m.price != null ? `₹${m.price}` : ""}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {g.status === "pending" && <>
                    <button onClick={() => act(g.group_id, "merge", { canonical_id: chosen })} data-testid={`dedupe-merge-${g.group_id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"><Check className="h-3 w-3" /> Merge (collapse in search)</button>
                    <button onClick={() => act(g.group_id, "ignore")} data-testid={`dedupe-ignore-${g.group_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><X className="h-3 w-3" /> Not duplicates</button>
                  </>}
                  {g.status === "merged" && <button onClick={() => act(g.group_id, "unmerge")} data-testid={`dedupe-unmerge-${g.group_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><RefreshCw className="h-3 w-3" /> Unmerge (restore all)</button>}
                  {g.status === "ignored" && <button onClick={() => act(g.group_id, "unmerge")} data-testid={`dedupe-reopen-${g.group_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><EyeOff className="h-3 w-3" /> Reopen</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
